import { useEffect, useRef, useState } from 'react';
import { BoltSmallRectangleColorVioletIcon, Button, ChevronLeftIcon, ClipboardCheckFillIcon, ConfettiFillIcon, DownloadIcon } from '@zep-us/zus';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useRouter } from 'next/router';
import Footer from '@/components/Footer';
import { type QuizQuoteResultData, quoteResultStorage, saveQuoteDocumentPdf } from '../utils';
import { QuizQuoteDocument } from './components/QuizQuoteDocument';
function formatKoreanDate(date: Date): string {
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}
function formatDocumentDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return formatKoreanDate(date);
}
function buildCheckoutUrl(quote: QuizQuoteResultData['quote']): string {
  const params = new URLSearchParams({
    plan: quote.plan.toUpperCase()
  });
  return `/checkout/method?${params.toString()}`;
}
function debugQuoteResult(message: string, payload?: unknown) {
  if (process.env.NEXT_PUBLIC_STAGE === 'PRD') return;

  // eslint-disable-next-line no-console
  console.debug(`[pricing/quote/result] ${message}`, payload);
}
export function QuoteResultPageView() {
  const router = useRouter();
  const {
    isReady,
    query: {
      key: queryKey
    },
    replace
  } = router;
  const quoteRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<QuizQuoteResultData | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  useEffect(() => {
    if (!isReady) return;
    const key = quoteResultStorage.getKeyFromQuery(queryKey);
    const stored = key ? quoteResultStorage.get(key) : quoteResultStorage.getLatest();
    const latestStored = stored ?? quoteResultStorage.getLatest();
    debugQuoteResult('restore result', {
      queryKey,
      normalizedKey: key,
      hasStoredResult: Boolean(latestStored)
    });
    if (!latestStored) {
      void replace('/pricing/quote');
      return;
    }
    setData(latestStored);
  }, [isReady, queryKey, replace]);
  if (!data) {
    return null;
  }
  const {
    quote,
    submittedPlanType,
    savedAt
  } = data;
  const today = formatDocumentDate(savedAt);
  const handleDownloadPdf = async () => {
    if (!quoteRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await saveQuoteDocumentPdf({
        element: quoteRef.current,
        scale: 3,
        imageFormat: 'PNG',
        quality: 1.0,
        fileName: `ZEP QUIZ 프로 플랜 견적서_${quote.schoolName}.pdf`,
        preloadImages: true,
        fixWindowSize: true
      });
    } catch (error) {
      debugQuoteResult('pdf download failed', error);
      window.alert('PDF 다운로드 중 문제가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  const handleCheckout = () => {
    window.open(buildCheckoutUrl(quote), '_blank', 'noopener,noreferrer');
  };
  return <div className="min-h-screen bg-neutral font-sans text-body-md-regular" data-sentry-component="QuoteResultPageView" data-sentry-source-file="QuoteResultPageView.tsx">
      <main className="bg-neutral pb-[80px]">
        <div className="mx-auto flex w-[1000px] justify-between px-0 pt-2xl max-tablet-lg:w-full max-tablet-lg:px-lg">
          <div className="flex flex-col gap-sm">
            <div className="flex gap-2xs">
              <ClipboardCheckFillIcon width={30} height={30} fill={ICON_COLOR.default} data-sentry-element="ClipboardCheckFillIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              <h1 className="text-heading-lg-extrabold text-default">
                견적서 발행 완료
              </h1>
            </div>
            {quote.isGroupAutoApplied ? <div className="flex items-center gap-2xs pl-3xs">
                <ConfettiFillIcon size="sm" fill={ICON_COLOR.positive} />
                <span className="text-body-md-semibold text-neutral">
                  학교·단체 40% 할인이 적용됐어요.
                </span>
              </div> : null}
          </div>
        </div>

        <div className="mx-auto mt-xl flex w-[1000px] justify-between gap-2xl px-0 max-tablet-lg:w-full max-tablet-lg:flex-col max-tablet-lg:px-lg">
          <div className="w-[560px] shrink-0 max-tablet-lg:w-full max-tablet-lg:max-w-[560px]">
            <QuizQuoteDocument ref={quoteRef} quote={quote} submittedPlanType={submittedPlanType} today={today} data-sentry-element="QuizQuoteDocument" data-sentry-source-file="QuoteResultPageView.tsx" />
          </div>

          <div className="w-[400px] shrink-0 max-tablet-lg:w-full max-tablet-lg:max-w-[560px]">
            <Button variant="contained" intent="alternative" size="lg" className="w-full" onClick={() => window.history.back()} data-sentry-element="Button" data-sentry-source-file="QuoteResultPageView.tsx">
              <ChevronLeftIcon size="md" data-sentry-element="ChevronLeftIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              견적서 정보 재입력
            </Button>
            <Button variant="contained" intent="strong" size="lg" className="mt-4 w-full" disabled={isGeneratingPdf} onClick={handleDownloadPdf} data-sentry-element="Button" data-sentry-source-file="QuoteResultPageView.tsx">
              <DownloadIcon size="md" data-sentry-element="DownloadIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              {isGeneratingPdf ? 'PDF 생성 중' : 'PDF 다운로드'}
            </Button>

            <div className="mt-md flex flex-col items-center gap-sm self-stretch rounded-md bg-default p-lg shadow-xs">
              <BoltSmallRectangleColorVioletIcon size="xl" data-sentry-element="BoltSmallRectangleColorVioletIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              <div className="text-center text-heading-md-bold text-default">
                플랜을 바로 시작해보세요
              </div>
              <div className="mb-sm text-center text-body-md-regular text-default">
                카드 결제는 바로 진행할 수 있어요. <br />
                계좌이체는 <strong>1:1 문의</strong>를 통해 도와드릴게요.
              </div>
              <Button variant="contained" intent="gradient" size="lg" className="w-full" onClick={handleCheckout} data-sentry-element="Button" data-sentry-source-file="QuoteResultPageView.tsx">
                결제 바로가기
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer className="bg-default" data-sentry-element="Footer" data-sentry-source-file="QuoteResultPageView.tsx" />
    </div>;
}